import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, CheckCircle, GripVertical } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface MetricOption {
  id: string;
  label: string;
  description: string;
}

interface MetricCustomizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (metrics: string[]) => void;
  currentMetrics: string[];
}

const availableMetrics: MetricOption[] = [
  { id: 'total_posts', label: 'Total Posts', description: 'Total number of posts across all platforms' },
  { id: 'engagement', label: 'Engagement', description: 'Average engagement rate across platforms' },
  { id: 'followers', label: 'Followers', description: 'Total follower count across platforms' },
  { id: 'views', label: 'Views', description: 'Total view count across platforms' },
  { id: 'ctr', label: 'Click-Through Rate', description: 'Average click-through rate on posts' },
  { id: 'reach', label: 'Reach', description: 'Total reach across all platforms' },
  { id: 'story_replies', label: 'Story Replies', description: 'Total replies to your stories' },
  { id: 'saves', label: 'Saves', description: 'Total number of saves/bookmarks' },
  { id: 'top_post', label: 'Top Performing Post', description: 'Your best performing post' },
  { id: 'new_comments', label: 'New Comments', description: 'Recent comments on your content' }
];

export const MetricCustomizeModal: React.FC<MetricCustomizeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentMetrics
}) => {
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(currentMetrics);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSelectedMetrics(currentMetrics);
  }, [currentMetrics]);

  const handleToggleMetric = (metricId: string) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) {
        // Always keep exactly 4 metrics
        if (prev.length <= 4) return prev;
        return prev.filter(id => id !== metricId);
      }
      // Don't allow more than 4 metrics
      if (prev.length >= 4) {
        return prev;
      }
      return [...prev, metricId];
    });
  };

  const handleResetToDefault = () => {
    setSelectedMetrics(['total_posts', 'engagement', 'followers', 'views']);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      // Ensure we always have exactly 4 metrics
      if (selectedMetrics.length !== 4) {
        setError("Please select exactly 4 metrics");
        return;
      }

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Update preferences in database
      const { error: updateError } = await supabase
        .from('dashboard_metric_preferences')
        .upsert({
          user_id: user.id,
          metrics: selectedMetrics
        }, {
          onConflict: 'user_id'
        });

      if (updateError) throw updateError;

      onSave(selectedMetrics);
      setSuccess(true);
      
      // Close modal after showing success message
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 1500);

    } catch (err) {
      console.error('Error saving metric preferences:', err);
      setError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    const newMetrics = [...selectedMetrics];
    const [removed] = newMetrics.splice(dragIndex, 1);
    newMetrics.splice(dropIndex, 0, removed);
    setSelectedMetrics(newMetrics);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 max-w-[100vw]"
          >
            <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-lg w-full max-w-[calc(100vw-2rem)]">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Customize Dashboard Metrics
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
                    <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
                  </div>
                )}

                {success && (
                  <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                    <p className="text-sm text-green-600 dark:text-green-300">
                      Preferences saved successfully!
                    </p>
                  </div>
                )}

                <div className="mb-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Select up to 4 metrics to display on your dashboard. Drag to reorder.
                  </p>
                </div>

                {/* Selected Metrics */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    Selected Metrics ({selectedMetrics.length}/4)
                  </h4>
                  <div className="space-y-2">
                    {selectedMetrics.map((metricId, index) => {
                      const metric = availableMetrics.find(m => m.id === metricId);
                      if (!metric) return null;
                      
                      return (
                        <div
                          key={metric.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, index)}
                          className="flex items-center bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg cursor-move group"
                        >
                          <GripVertical className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {metric.label}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {metric.description}
                            </p>
                          </div>
                          <button
                            onClick={() => handleToggleMetric(metric.id)}
                            className="ml-2 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Available Metrics */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    Available Metrics
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {availableMetrics
                      .filter(metric => !selectedMetrics.includes(metric.id))
                      .map(metric => (
                        <button
                          key={metric.id}
                          onClick={() => handleToggleMetric(metric.id)}
                          disabled={selectedMetrics.length >= 4}
                          className={`p-3 text-left border border-gray-200 dark:border-dark-700 rounded-lg hover:border-indigo-500 dark:hover:border-indigo-400 transition-colors ${
                            selectedMetrics.length >= 4
                              ? 'opacity-50 cursor-not-allowed'
                              : 'cursor-pointer'
                          }`}
                        >
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {metric.label}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {metric.description}
                          </p>
                        </button>
                      ))}
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={handleResetToDefault}
                    className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    Reset to Default
                  </button>
                  <div className="space-x-3">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={loading}
                      className={`px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};